// ─── Web Push (the "App" notification channel, DECISIONS §13) ─────────────────
// Browser-side helpers: what this device can do, and subscribe/unsubscribe
// against /api/notifications/push/*. The service worker lives at /sw.js.
//
// iPhone/iPad only get Web Push from a Home Screen app on iOS/iPadOS 16.4+.
// In Safari itself PushManager isn't exposed, so "unsupported" there really
// means "add to Home Screen first".

import { apiClient } from './api'

export type PushSupport =
  | 'unsupported'        // no service worker / PushManager / Notification
  | 'ios-too-old'        // iOS/iPadOS below 16.4: no Web Push at all
  | 'ios-not-installed'  // iOS 16.4+ in Safari: needs Add to Home Screen first
  | 'default'            // can ask
  | 'granted'
  | 'denied'

export interface PushEnv {
  userAgent: string
  maxTouchPoints: number
  standalone: boolean
  hasServiceWorker: boolean
  hasPushManager: boolean
  permission: NotificationPermission | null
}

export function readPushEnv(): PushEnv {
  const w = typeof window !== 'undefined' ? window : undefined
  const nav = typeof navigator !== 'undefined' ? navigator : undefined
  let standalone = false
  try {
    standalone =
      (nav as Navigator & { standalone?: boolean } | undefined)?.standalone === true ||
      (w?.matchMedia?.('(display-mode: standalone)').matches ?? false)
  } catch {
    standalone = false
  }
  return {
    userAgent: nav?.userAgent ?? '',
    maxTouchPoints: nav?.maxTouchPoints ?? 0,
    standalone,
    hasServiceWorker: !!nav && 'serviceWorker' in nav,
    hasPushManager: !!w && 'PushManager' in w,
    permission: w && 'Notification' in w ? Notification.permission : null,
  }
}

/** iOS/iPadOS version from the UA, or null when not an Apple touch device. */
export function iosVersion(env: Pick<PushEnv, 'userAgent' | 'maxTouchPoints'>): number | null {
  const ua = env.userAgent
  const m = ua.match(/(?:iPhone|iPad|iPod).*? OS (\d+)[._](\d+)/)
  if (m) return Number(m[1]) + Number(m[2]) / 100
  // iPadOS 13+ reports itself as a Mac; touch points give it away.
  if (/Macintosh/.test(ua) && env.maxTouchPoints > 1) {
    const v = ua.match(/Version\/(\d+)\.(\d+)/)
    return v ? Number(v[1]) + Number(v[2]) / 100 : 99
  }
  return null
}

export function detectPushSupport(env: PushEnv = readPushEnv()): PushSupport {
  const ios = iosVersion(env)
  if (ios !== null) {
    if (ios < 16.04) return 'ios-too-old'
    if (!env.standalone) return 'ios-not-installed'
  }
  if (!env.hasServiceWorker || !env.hasPushManager || env.permission === null) return 'unsupported'
  return env.permission
}

export interface PushConfig {
  configured: boolean
  publicKey: string | null
}

export interface PushDevice {
  id: string
  endpoint: string
  device: string
  createdAt: string
  lastUsedAt: string | null
}

export async function fetchPushConfig(): Promise<PushConfig> {
  const res = await apiClient.get<{ data: PushConfig }>('/api/notifications/push/config')
  return res.data.data
}

export async function fetchPushDevices(): Promise<PushDevice[]> {
  const res = await apiClient.get<{ data: { items: PushDevice[] } }>('/api/notifications/push/subscriptions')
  return res.data.data.items
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(padded)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/')
  if (existing) return existing
  await navigator.serviceWorker.register('/sw.js', { scope: '/' })
  return navigator.serviceWorker.ready
}

/** This browser's current push subscription, if any. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) return null
  const reg = await navigator.serviceWorker.getRegistration('/')
  return (await reg?.pushManager.getSubscription()) ?? null
}

/**
 * Ask for permission (must run from a tap), subscribe this browser and save it
 * on the server. Returns the resulting permission.
 */
export async function enablePush(publicKey: string): Promise<NotificationPermission> {
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission
  const reg = await registration()
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    }))
  const json = sub.toJSON()
  await apiClient.post('/api/notifications/push/subscribe', {
    endpoint: json.endpoint,
    keys: json.keys,
  })
  return permission
}

/** Stop push on this browser, locally and on the server. */
export async function disablePush(): Promise<void> {
  const sub = await currentSubscription()
  if (!sub) return
  const endpoint = sub.endpoint
  await sub.unsubscribe().catch(() => false)
  await apiClient.post('/api/notifications/push/unsubscribe', { endpoint })
}
